import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, Share, Plus, X, Bell, Maximize, Zap } from "lucide-react";

const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;

function isStandalone() {
  return (window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone) || false;
}

// One-time "Install app" popup for signed-in members. Hides forever once the
// app is installed (standalone or `appinstalled`), snoozes 7 days on dismiss.
export function InstallPrompt({ auto = true }) {
  const [open, setOpen] = useState(false);
  const [showSteps, setShowSteps] = useState(false);
  const isIOS = /iphone|ipad|ipod/i.test((typeof navigator !== "undefined" && navigator.userAgent) || "");

  useEffect(() => {
    if (isStandalone()) return;
    let t;
    if (auto) {
      try {
        if (localStorage.getItem("sp-installed")) return;
        const d = Number(localStorage.getItem("sp-install-dismissed") || 0);
        if (d && Date.now() - d < SNOOZE_MS) return;
      } catch { /* ignore */ }
      t = setTimeout(() => setOpen(true), 1200);
    }
    const onInstalled = () => { try { localStorage.setItem("sp-installed", "1"); } catch { /* ignore */ } setOpen(false); };
    window.addEventListener("appinstalled", onInstalled);
    // Force-open on demand (sandbox "Keep it" signup, or a worker right after they send proof back) — ignores snooze.
    const force = () => { if (!isStandalone()) { setShowSteps(false); setOpen(true); } };
    window.addEventListener("sp:install-prompt", force);
    return () => { if (t) clearTimeout(t); window.removeEventListener("appinstalled", onInstalled); window.removeEventListener("sp:install-prompt", force); };
  }, [auto]);

  const dismiss = () => {
    try { localStorage.setItem("sp-install-dismissed", String(Date.now())); } catch { /* ignore */ }
    setOpen(false);
  };

  const install = async () => {
    const deferred = window.__deferredInstallPrompt;
    if (deferred) {
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      window.__deferredInstallPrompt = null;
      if (outcome === "accepted") { try { localStorage.setItem("sp-installed", "1"); } catch { /* ignore */ } setOpen(false); }
      else dismiss();
      return;
    }
    setShowSteps(true);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/60 backdrop-blur-sm sm:items-center" data-testid="install-prompt-modal" onClick={dismiss}>
      <div
        className="animate-in-up w-full rounded-t-3xl border border-border bg-card p-6 sm:max-w-sm sm:rounded-3xl"
        onClick={(e) => e.stopPropagation()}
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 1.5rem)" }}
      >
        <div className="flex items-start justify-between">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-500 text-white"><Download className="h-6 w-6" /></div>
          <button onClick={dismiss} data-testid="install-prompt-close" className="text-muted-foreground/70 hover:text-foreground"><X className="h-5 w-5" /></button>
        </div>

        {!showSteps ? (
          <>
            <h3 className="mt-4 font-heading text-xl font-bold">Install Send-Proof</h3>
            <p className="mt-1 text-sm text-muted-foreground">Add it to your home screen for the full app — no browser bars, instant open, and live proof notifications.</p>
            <ul className="mt-4 space-y-2.5 text-sm">
              <li className="flex items-center gap-2.5"><Maximize className="h-4 w-4 text-emerald-500" /> Full-screen, native feel</li>
              <li className="flex items-center gap-2.5"><Bell className="h-4 w-4 text-emerald-500" /> Live “proof sent back” alerts</li>
              <li className="flex items-center gap-2.5"><Zap className="h-4 w-4 text-emerald-500" /> Opens instantly from your home screen</li>
            </ul>
            <Button data-testid="install-prompt-install-btn" onClick={install} className="mt-5 h-12 w-full gap-2 bg-emerald-500 text-base hover:bg-emerald-600">
              <Download className="h-4 w-4" /> Install app
            </Button>
            <button data-testid="install-prompt-later-btn" onClick={dismiss} className="mt-2 h-10 w-full text-sm font-medium text-muted-foreground">Not now</button>
          </>
        ) : (
          <>
            <h3 className="mt-4 font-heading text-xl font-bold">Add to Home Screen</h3>
            <p className="mt-1 text-sm text-muted-foreground">A couple of taps and it lives on your phone like a real app.</p>
            <ol className="mt-4 space-y-3 text-sm">
              <li className="flex items-center gap-2"><span className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-500/15 text-xs font-bold text-emerald-500">1</span> Tap the {isIOS ? <Share className="mx-1 inline h-4 w-4" /> : <span className="mx-1 font-bold">⋮</span>} {isIOS ? "Share" : "menu"} button.</li>
              <li className="flex items-center gap-2"><span className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-500/15 text-xs font-bold text-emerald-500">2</span> Choose <b className="mx-1 inline-flex items-center gap-1"><Plus className="h-3.5 w-3.5" /> Add to Home Screen</b>.</li>
              <li className="flex items-center gap-2"><span className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-500/15 text-xs font-bold text-emerald-500">3</span> Tap <b className="mx-1">Add</b> — done!</li>
            </ol>
            <Button data-testid="install-prompt-gotit-btn" onClick={dismiss} className="mt-5 h-12 w-full bg-emerald-500 text-base hover:bg-emerald-600">Got it</Button>
          </>
        )}
      </div>
    </div>
  );
}
